import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type GlobalAnalysis = {
  generated_at: string;
  stats: {
    records: number;
    exams: number;
    documents: number;
    alerts?: number;
  };
  summary: string;
  highlights: {
    type: "warning" | "success" | "info";
    title: string;
    message: string;
  }[];
  sections: {
    title: string;
    bullets: string[];
  }[];
  systems?: Array<{
    name: string;
    status: "excellent" | "good" | "attention" | "critical";
    metrics: number;
    alerts: number;
    summary?: string;
    detailed_metrics?: Array<{
      name: string;
      value: string | number;
      unit?: string;
      status: "normal" | "abnormal";
      reference?: string;
    }>;
  }>;
  recommendations?: Array<{
    priority: "high" | "medium" | "low";
    title: string;
    description: string;
    category: "medication" | "lab" | "lifestyle" | "followup" | "specialist";
  }>;
  completeness?: {
    score: number;
    breakdown: {
      pct_value: number;
      pct_refs: number;
      pct_unit: number;
      pct_date: number;
    };
    missing: string[];
  };
  health_score?: number;
  health_score_breakdown?: {
    score: number;
    components: {
      lab_score: number;
      lab_weight: number;
      completeness_score: number;
      completeness_weight: number;
      vital_score: number;
      vital_weight: number;
    };
    metrics: {
      total_lab_results: number;
      abnormal_results: number;
      abnormal_percentage: number;
      vital_signs_count: number;
      completeness_percentage: number;
    };
  };
  active_alerts?: Array<{
    severity: "critical" | "warning" | "info";
    title: string;
    message: string;
    source: string;
  }>;
  clinical_timeline?: Array<{
    date: string;
    title: string;
    description: string;
    trend?: "improving" | "worsening" | "stable";
    category: string;
  }>;
  trends?: unknown;
  problems?: string[];
  medications?: string[];
};

interface GlobalAnalysisParams {
  patientId: string;
}

export function useGlobalAnalysisQuery(patientId: string) {
  return useQuery({
    queryKey: ['global-analysis', patientId],
    queryFn: async (): Promise<GlobalAnalysis | null> => {
      console.log("🔍 Fetching global analysis from cache for patient:", patientId);

      // Buscar DIRETO do cache no banco
      const { data: cacheData, error } = await supabase
        .from('ai_global_analysis_cache')
        .select('json_result')
        .eq('patient_id', patientId)
        .maybeSingle();

      if (error) {
        console.error("❌ Error fetching from cache:", error);
        throw error;
      }

      if (!cacheData?.json_result) {
        console.log("ℹ️ No cached analysis found");
        return null;
      }

      const data = cacheData.json_result as GlobalAnalysis;

      console.log("✅ Global analysis fetched from cache:", {
        hasSummary: !!data?.summary,
        summaryLength: data?.summary?.length,
        summaryPreview: data?.summary?.substring(0, 100),
        summaryFull: data?.summary,
        hasHighlights: !!data?.highlights,
        highlightsCount: data?.highlights?.length,
        problems_count: data?.problems?.length || 0,
        medications_count: data?.medications?.length || 0,
        problems: data?.problems,
        medications: data?.medications
      });

      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    enabled: !!patientId,
  });
}

export function useGlobalAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ patientId }: GlobalAnalysisParams): Promise<GlobalAnalysis> => {
      console.log("🔄 Generating global analysis for patient:", patientId);

      const { data, error } = await supabase.functions.invoke("generate-global-analysis", {
        body: { patient_id: patientId, force_regenerate: true },
      });

      if (error) {
        console.error("❌ Error generating global analysis:", error);
        throw error;
      }

      console.log("✅ Global analysis generated successfully:", {
        hasSummary: !!data?.summary,
        summaryLength: data?.summary?.length,
        summaryPreview: data?.summary?.substring(0, 100),
        hasHighlights: !!data?.highlights,
        highlightsCount: data?.highlights?.length,
        problems_count: data?.problems?.length || 0,
        medications_count: data?.medications?.length || 0,
        problems: data?.problems,
        medications: data?.medications,
        fullResponse: data
      });
      return data as GlobalAnalysis;
    },
    onSuccess: (data, variables) => {
      console.log("✅ Global analysis generated:", {
        problems_count: data?.problems?.length || 0,
        medications_count: data?.medications?.length || 0,
        problems: data?.problems,
        medications: data?.medications,
        patientId: variables.patientId
      });
      
      // Invalidar query para forçar refresh
      queryClient.invalidateQueries({ queryKey: ['global-analysis', variables.patientId] });
      
      // Forçar refetch imediato para atualizar a UI
      queryClient.refetchQueries({ queryKey: ['global-analysis', variables.patientId] });
      
      toast.success("✨ Análise global gerada com sucesso!");
      console.log("📊 Analysis stats:", data.stats);
    },
    onError: (error: any) => {
      const message = error?.message ?? "Falha ao gerar análise global";
      console.error("❌ Global analysis error:", message);
      toast.error(message);
    },
  });
}
